import { Checkbox } from '@remote-com/mushu/src/components/Ui/Form/Checkbox';
import { InputLabel } from '@remote-com/mushu/src/components/Ui/Form/Input';
import { CheckboxState } from '@remote-com/mushu/src/domains/registration/onboarding/shared/styles/Form';
import { ButtonMain } from '@remote-com/norma/core/button/Button.styled';
import { ButtonOutline } from 'remote-com-shared/components/Button';
import { TitleComponent } from 'remote-com-shared/components/ClientLogos';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { Hero } from 'remote-com-shared/components/Hero';
import {
  Check,
  FeaturesList,
  FeaturesListEntry,
} from 'remote-com-shared/components/Hero/FeaturesList';
import {
  ContentLayout,
  Title,
  ImageBackgroundStyle,
  WideDescription,
  Actions,
  ContentMain,
} from 'remote-com-shared/components/Hero/styles';
import {
  FormContainer,
  DynamicFormWrapper,
  ModalTitle,
  ModalDescription,
} from 'remote-com-shared/components/ModalForm/styles';
import { StepCounter, SubmitButton } from 'remote-com-shared/components/TwoStepForm/styles';
import { staticAssetUrl } from 'remote-com-shared/domains/assets/helpers';
import { themeColorTones } from 'remote-com-shared/style/colors';
import { fluidProp, whenProp, focusVisible } from 'remote-com-shared/style/helpers';
import { breakpointFrom, breakpointUntil } from 'remote-com-shared/style/mediaQueries';
import styled, { css } from 'styled-components';

import HeroBackgroundImage from '../assets/hero-illustration.svg';
import FrenchTrustpilotLogo from '../assets/trustpilot-fr.svg';
import TrustpilotLogo from '../assets/trustpilot-logo.svg';

import { BaseCell } from '@/src/domains/pricing/PricingOverviewSection';
import {
  tabletBreakpoint,
  fromTabletBreakpoint,
  fromDesktopBreakpoint,
} from '@/src/domains/shared/breakpoints';

// Hero
export const HeaderCTAButton = styled(ButtonOutline)`
  ${({ theme: { colorTone } }) => {
    // for "purple-blue", the "blue" needs to be actually turquoise
    if (colorTone === 'purple-blue') {
      return css`
        --color-tone-secondary: var(--colors-turquoise);
      `;
    }
    return css`
      --color-tone-secondary: ${themeColorTones[colorTone].secondary};
    `;
  }}

  && {
    border-color: var(--color-tone-secondary, var(--colors-turquoise));

    ${whenProp(
      '$primaryTone',
      css`
        background-color: var(--colors-irisBlue);
      `
    )}

    ${whenProp(
      '$frenchLocale',
      css`
        border-color: var(--colors-blank);

        &:hover {
          border-color: #0564ff;
        }
      `
    )}
  }
`;

export const HeroBackgroundIllustration = styled(HeroBackgroundImage)`
  ${ImageBackgroundStyle}

  ${tabletBreakpoint(css`
    display: none;
  `)}
  ${fromTabletBreakpoint(css`
    right: 0;
    bottom: 0;
  `)}

  color: var(--color-tone-secondary, #0261FE);
`;

export const CustomHeroWrapper = styled.div`
  ${ImageBackgroundStyle}
  display: flex;
  left: 0;
  top: 0;
  width: 100vw;
  height: 89vh;

  &[data-show-custom-hero='false'] {
    display: none;
  }
  &[data-show-custom-hero='true'] + svg {
    display: none;
  }

  & > span {
    width: 650px;
    background-color: #a49aff;
    clip-path: ellipse(250px 35% at -20% center);
  }

  ${breakpointUntil.ml(css`
    display: none;
  `)}
`;

export const CustomHeroImageWrapper = styled.div`
  ${ImageBackgroundStyle}
  display: flex;
  right: 0;
  top: 0;
  clip-path: ellipse(100% 50% at right center);

  & > img {
    max-height: 100vh;
  }
`;

export const HeroDescription = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  font-family: var(--typography-fontBrand);
  margin-top: 42px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }

  ul,
  ol {
    margin-top: 46px;
    list-style-type: none;
  }

  li {
    margin-top: 20px;
    position: relative;

    &::after {
      content: '';
      background-color: white;
      -webkit-mask-image: url(${staticAssetUrl('/images/check-purple.svg')});
      mask-image: url(${staticAssetUrl('/images/check-purple.svg')});
      top: 10px;
      left: -30px;
      width: 15px;
      height: 10px;
      position: absolute;
      z-index: 2;
    }
  }
`;

export const GetStartedFeaturesList = styled(FeaturesList)`
  margin-top: 0;
  flex-direction: column;
  align-items: flex-start;
  color: #b7aeef;
`;
export const GetStartedFeaturesListEntry = styled(FeaturesListEntry)`
  color: var(--colors-alabaster);

  ${Check} {
    fill: var(--color-tone-secondary, #0261fe);
  }

  ${breakpointFrom.lg(css`
    flex-direction: row;
    align-items: center;

    && {
      & + & {
        margin-left: 0;
        margin-top: 15px;
      }
    }
  `)};

  ${whenProp(
    '$frenchLocale',
    css`
      color: var(--colors-blank);
      ${Check} {
        fill: var(--colors-blank);
        border: 1px solid var(--colors-blank);
        border-radius: 50px;
        min-width: 21px;
        width: 21px;
        height: 21px;
        padding: 3px;
        box-sizing: border-box;
      }
    `
  )}
`;

export const BadgesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
`;

export const RecognitionBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  justify-content: start;
  ${fluidProp(['margin-top'], '25px', '35px')}
  ${fluidProp(['margin-bottom'], '20px', '10px')}
  ${fluidProp(['gap'], '5px', '15px')}
  margin-left: unset;
`;

export const Badge = styled.div`
  min-width: clamp(130px, 40vw, 160px);
  min-height: clamp(130px, 40vw, 160px);
  position: relative;
  display: flex;
  margin-left: unset;
`;

export const TrustpilotRatingWrapper = styled.span`
  display: flex;
  gap: 20px;
  align-items: center;
  position: relative;
  margin-left: unset;
`;

export const RatingStars = styled.span`
  ${({ $rawValue }) => {
    if (typeof $rawValue === 'undefined') {
      return '';
    }

    const [number, decimal] = $rawValue.split('.').map((s) => parseInt(s, 10));

    let decimalFactor;
    switch (decimal) {
      case 9:
        decimalFactor = 15;
        break;
      case 8:
        decimalFactor = 14;
        break;
      case 7:
        decimalFactor = 13;
        break;
      case 6:
        decimalFactor = 12;
        break;
      case 5:
        decimalFactor = 10;
        break;
      case 4:
        decimalFactor = 9;
        break;
      case 3:
        decimalFactor = 8;
        break;
      case 2:
        decimalFactor = 7;
        break;
      case 1:
        decimalFactor = 6;
        break;
      default:
        decimalFactor = 0;
        break;
    }
    const valueActual = number * 20 + decimalFactor;

    return css`
      --value: ${valueActual}%;
    `;
  }}

  display: inline-block;
  margin-left: unset;
  margin-top: 5px;
  font-size: 1.8rem;
  line-height: 1.8rem;

  ${({ $background }) => {
    if ($background)
      return css`
        &::before {
          background-color: var(--color-tone-primary, var(--colors-royalPurple));
          filter: drop-shadow(1px 0px 0px var(--color-tone-secondary, var(--colors-turquoise)))
            drop-shadow(0px 1px 0px var(--color-tone-secondary, var(--colors-turquoise)))
            drop-shadow(0px -1px 0px var(--color-tone-secondary, var(--colors-turquoise)))
            drop-shadow(-1px 0px 0px var(--color-tone-secondary, var(--colors-turquoise)));
        }
      `;

    return css`
      position: absolute;

      &::before {
        background: linear-gradient(
          90deg,
          var(--color-tone-secondary, var(--colors-turquoise)) var(--value),
          transparent var(--value)
        );
      }
    `;
  }}

  &::before {
    content: '★★★★★';
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const StyledTrustpilotLogo = styled(TrustpilotLogo)`
  margin-left: 0;
`;

export const StyledFrenchTrustpilotLogo = styled(FrenchTrustpilotLogo)`
  margin-left: 0;
`;

export const GetStartedHero = styled(Hero)`
  --darkBrownColor: #15003e;
  --seaBlueColor: #0564ff;

  background-color: var(--color-tone-primary, var(--newBrandColor));
  background-image: unset;

  ${ContentLayout} {
    display: grid;
    align-items: start;
    grid-template-columns: 1fr;
    gap: 50px;

    ${fromTabletBreakpoint(css`
      grid-template-columns: 1fr 435px;
    `)};
  }
  ${Title} {
    ${fluidProp('font-size', '34px', '60px')}
  }
  ${WideDescription} {
    color: var(--colors-blank);
    ${fluidProp('margin-top', '12px', '24px')}
  }
  ${Actions} {
    ${fluidProp('margin-top', '12px', '24px')}

    * + * {
      margin-left: unset;

      ${breakpointUntil.sm(css`
        margin-top: unset;
        margin-right: unset;
        margin-left: unset;
      `)}
    }

    ${({ theme: { colorTone } }) => {
      // for "purple-blue", the "blue" needs to be actually turquoise
      if (colorTone === 'purple-blue') {
        return css`
          --color-tone-secondary: var(--colors-turquoise);

          &::before {
            background: linear-gradient(
              90deg,
              var(--color-tone-secondary, var(--colors-turquoise)) var(--value),
              red var(--value)
            );
          }
        `;
      }
      return '';
    }}
  }

  * {
    font-family: var(--typography-fontBrand);
  }

  ${whenProp(
    '$frenchLocale',
    css`
      background-image: url(${({ image }) => image});
      background-size: cover;
      background-position: center;

      ${Title} {
        font-family: var(--typography-fontBossa);
      }

      ${HeroDescription} {
        font-size: 16px;
      }

      ${RatingStars} {
        --color-tone-secondary: #92c969;
        --color-tone-primary: var(--colors-darkBlue);
      }

      ${FormContainer} {
        padding: 42px 32px;
        border-radius: 16px;
        max-width: 465px;
      }

      ${fromDesktopBreakpoint(css`
        ${ContentContainer} {
          max-width: 1350px;
        }
        ${ContentMain} {
          max-width: 605px;
        }
      `)};

      ${Badge} {
        min-width: 125px;
      }

      ${BadgesContainer} {
        gap: 25px;
      }

      ${DynamicFormWrapper} {
        padding-top: 0;
      }

      ${ModalTitle} {
        font-family: var(--typography-fontBossa);
        ${({ theme }) => theme.typography.titleSM};
      }

      ${ModalDescription} {
        color: #4e6783;
      }
      && {
        && {
          ${InputLabel} {
            span {
              --topLabelColor: var(--darkBrownColor);
            }
            input {
              --inputBorderColor: var(--darkBrownColor);
              --placeholderColor: var(--darkBrownColor);
              --inputBorderSize: 1px;

              ${focusVisible(css`
                --inputBorderSize: 2px;
              `)}
            }

            #where_is_the_company_established___localised_,
            #reason_localised,
            #target_countries {
              div {
                border-color: var(--darkBrownColor) !important;
                border-width: 1px !important;

                &:focus-visible,
                &:hover,
                &:focus-within {
                  border-width: 2px !important;
                }
              }
            }
          }
          ${CheckboxState} {
            border-color: #778ca3;

            &:after {
              background-color: var(--seaBlueColor);
            }
          }
          ${Checkbox} {
            a {
              color: var(--seaBlueColor);
              text-decoration: underline;
              text-decoration-color: var(--seaBlueColor);
              text-underline-offset: 3px;
            }
          }
          ${SubmitButton} {
            background-color: var(--seaBlueColor);
          }
        }
      }

      ${ButtonMain} {
        --button-colorText: #fceef1;
        background-color: var(--seaBlueColor);
        padding: 18px 24px;
      }

      ${StepCounter} {
        color: var(--darkBrownColor);
      }
    `
  )}
`;

export const Main = styled.main`
  * {
    font-family: var(--typography-fontBrand);
  }
  ${({ theme: { colorTone } }) => css`
    --color-tone-primary: ${themeColorTones[colorTone].primary};
    --color-tone-secondary: ${themeColorTones[colorTone].secondary};
  `}
  --borderColor: var(--color-tone-secondary);

  ${BaseCell} {
    --colors-irisBlue: var(--newBrandColor);
    --primaryLinearGradient: none;
  }

  ${whenProp(
    '$frenchLocale',
    css`
      ${TitleComponent} {
        max-width: unset;
        ${({ theme }) => theme.typography.scale4};

        > * {
          font-family: var(--typography-fontBossa);
        }
      }
    `
  )}
`;
